import { HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { AuthService } from '@features/auth/services/auth.service';
import { environment } from '@environments/environment';

export function authInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn) {
  const authService = inject(AuthService);
  const authToken = authService.getToken();

  // Skip adding auth header for auth-related requests
  if (isAuthRequest(request)) {
    return next(request);
  }

  if (!authToken) return next(request);

  const authenticatedRequest = request.clone({
    headers: request.headers.set('Authorization', `Bearer ${authToken}`)
  });

  return next(authenticatedRequest);
}

// ✅ Check if request is auth-related or public (login, refresh, business-registration, etc.)
function isAuthRequest(request: HttpRequest<unknown>): boolean {
  const url = request.url;
  const method = request.method;

  // Always-public endpoints (any method)
  const alwaysPublic = [
    '/auth/login',
    '/auth/otp',
    '/auth/refresh-token',
    '/business-registration',
    '/individuals-registration',
    '/individuals-registration-v2',
    // BE-111: coordinator email reject/approve pages — token-authed, no JWT.
    '/permission-requests/actions',
    '/api/v3/banks',
    '/public'
  ];
  if (alwaysPublic.some(endpoint => url.includes(endpoint))) return true;

  // Method-specific public endpoints:
  // POST /registration-requests  = public form submission (no auth needed)
  if (url.includes('/registration-requests') && method === 'POST') return true;

  // POST/PUT /upload/* = file upload for the public registration form.
  // Anchored to the start of the API path on purpose: a bare `includes('/upload')`
  // also matched authenticated endpoints that merely end in "upload"
  // (e.g. /admin/marketplace/payment-token-orders/bulk-approve/upload), which then
  // went out with no Authorization header and came back 401.
  // `/upload/file` (direct multipart upload, e.g. push-notification images) is
  // authenticated on the BE, so it must keep the JWT.
  const path = apiPath(url);
  if (path.startsWith('/upload/') && !path.startsWith('/upload/file') && (method === 'POST' || method === 'PUT')) return true;

  // Direct Cloud Storage uploads (signed URLs) and external APIs
  const externalDomains = [
    'storage.googleapis.com',
    'storage.cloud.google.com',
    'amazonaws.com',
    'nominatim.openstreetmap.org',
    'maps.googleapis.com'
  ];
  if (externalDomains.some(domain => url.includes(domain))) return true;

  return false;
}

/** The endpoint path as the services write it, i.e. with the environment base URL removed. */
function apiPath(url: string): string {
  return url.startsWith(environment.apiUrl) ? url.slice(environment.apiUrl.length) : url;
}